import axios from 'axios';
import { auth } from 'src/lib/firebase';
import { forceUpdateAndReload } from 'src/serviceWorkerRegistration';
import { trackRequest, untrackRequest } from 'src/utils/pendingRequests';

interface metadata {
  userId: string | undefined;
  companyId: string | undefined;
}

const defaultMetadata: metadata = {
  userId: null,
  companyId: null,
};

// Backend header naming the oldest FE build (epoch ms) it still supports.
// When this running build is older, a breaking BE change shipped mid-session:
// pick up the new FE version immediately.
const MIN_FE_BUILD_HEADER = 'x-riw-min-fe-build';

const checkMinFeBuild = (headers: any) => {
  const minBuild = Number(headers?.[MIN_FE_BUILD_HEADER]);
  if (Number.isFinite(minBuild) && minBuild > 0 && __BUILD_TIME__ < minBuild) {
    forceUpdateAndReload();
  }
};

const authAxios = async (metadata = defaultMetadata) => {
  let firebaseIdToken = null;

  let axiosRequestConfig = {
    baseURL: import.meta.env.VITE_BE_APP_URL,
    headers: {
      Authorization: `Bearer ${firebaseIdToken}`,
    },
  };

  if (metadata) {
    axiosRequestConfig.headers['x-riw-metadata'] = metadata;
  }

  try {
    // Use the cached ID token; the SDK refreshes it automatically as expiry
    // approaches. Company access is authorized server-side per request from
    // Firestore, so a cached token carries no stale COMPANY access.
    // Identity-level revocation (account disabled, refresh tokens revoked)
    // keeps an already-minted token working for up to ~1h — the backend's
    // verifyIdToken doesn't checkRevoked, so that server-side window existed
    // before this change too. Skipping the forced refresh saves a network
    // round trip on every call.
    const user = auth.currentUser;
    if (user) {
      let firebaseToken = await user.getIdToken();

      // One exception: the backend rejects every route whose token claim
      // says email_verified=false. When the email was verified outside this
      // session (the link opened in a mail client or on another device), the
      // profile flips to verified but the cached token's claim lags until
      // natural expiry. getIdTokenResult decodes locally (no network), so
      // detect that one mismatch and refresh only then.
      if (user.emailVerified) {
        const { claims } = await user.getIdTokenResult();
        if (claims.email_verified !== true) {
          firebaseToken = await user.getIdToken(true);
        }
      }

      axiosRequestConfig.headers.Authorization = `Bearer ${firebaseToken}`;
    }
  } catch (err) {}

  const instance = axios.create(axiosRequestConfig);

  instance.interceptors.request.use((config) => {
    trackRequest();
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      untrackRequest();
      checkMinFeBuild(response.headers);
      return response;
    },
    (error) => {
      untrackRequest();
      checkMinFeBuild(error?.response?.headers);
      return Promise.reject(error);
    }
  );

  return instance;
};

export default authAxios;
